import { useEffect, useState } from 'react';

/**
 * Putting the portal on a phone's home screen.
 *
 * Android fires `beforeinstallprompt` and lets us show a real button. iOS
 * never has and never will, so there the only honest answer is to say which
 * two taps to make — and on iOS it is not optional: Safari withholds
 * notifications until the portal is installed.
 */

export type InstallState =
  /** Already running from the home screen, or on a desktop that has installed it. */
  | 'installed'
  /** The browser offered us a prompt; we can show a button. */
  | 'ready'
  /** iOS: Share, then Add to Home Screen. */
  | 'ios'
  /** Android without a prompt (usually already dismissed once): menu, then Install. */
  | 'android'
  /** A desktop browser, where none of this matters. */
  | 'desktop';

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches
  || (window.navigator as { standalone?: boolean }).standalone === true;

export const isIos = (): boolean => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return false;
  // An iPad on iPadOS reports itself as a Mac and is given away by touch points.
  return /iphone|ipad|ipod/i.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isPhone = (): boolean => isIos() || /android/i.test(navigator.userAgent);

export function useInstall(): { state: InstallState; install: () => Promise<void> } {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Chrome shows its own bar otherwise, at a moment of its choosing.
      e.preventDefault();
      setEvent(e as InstallEvent);
    };
    const onInstalled = () => { setInstalled(true); setEvent(null); };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const state: InstallState = installed ? 'installed'
    : event ? 'ready'
    : isIos() ? 'ios'
    : isPhone() ? 'android'
    : 'desktop';

  const install = async (): Promise<void> => {
    if (!event) return;
    await event.prompt();
    const { outcome } = await event.userChoice;
    // A prompt is good once; Chrome will fire a fresh event if they change
    // their mind later.
    setEvent(null);
    if (outcome === 'accepted') setInstalled(true);
  };

  return { state, install };
}
